import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { compose } from 'recompose'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { fromJS, Map, List } from 'immutable'
import _partial from 'lodash/partial'
import _omit from 'lodash/omit'
import _get from 'lodash/get'
import _isFunction from 'lodash/isFunction'
import { connect as connectRedux } from 'react-redux'
import FormsyForm from 'formsy-react'
import LoginUsername from '../LoginUsername/index'
import LoginPassword from '../LoginPassword/index'
import ButtonSignUp, { BUTTON_SIGN_UP_TYPE_LINK } from '../ButtonSignUp/index'
import Link, { TARGET_BLANK } from '../Link/index'
import Loading, { LOADING_ICON_BLUE_DARK_BG_WHITE } from '../Loading/index'
import ErrorMessage from '../ErrorMessage/index'
import { get as getConfig } from '../../config/index'
import { URL_HELP_CENTER_ES } from '../../services/url/constants'
import { ES } from '../../services/languages/constants'
import { getPrimary } from '../../services/languages/index'
import { getBoundActions } from '../../actions/index'
import connectRouter from '../Router/connect'
import { TYPE_FORGOT_PASSWORD, TYPE_LOGIN_WITH_CODE } from '../../services/dialog/index'
import {
  historyRedirect,
  HISTORY_METHOD_REPLACE,
} from '../../services/navigation/index'
import { LOGIN_CODE_UNRECOGNIZED_USERNAME_PASSWORD } from '../../services/auth/login-codes'
import { H2, HEADING_TYPES } from '../Heading/index'
import Button, { TYPES } from '../Button/index'
import { USER_LOGIN_NEW_TO_GAIA_CLICK_GA4_EVENT } from '../../services/event-tracking/index'

const { features } = getConfig()
const loginWithCodeEnabled = _get(features, 'loginWithCode')

async function onValidSubmit (model, props) {
  const {
    doAuthLogin,
    setLoginMessages,
    setLoginMessageCodes,
  } = props
  const {
    username,
    password,
  } = model
  setLoginMessages(null)
  setLoginMessageCodes(null)
  try {
    const auth = await doAuthLogin({ username, password })
    if (_get(auth, 'success')) {
      onLoginSuccess(props, auth)
    } else {
      setLoginMessages(_get(auth, 'messages', []))
      setLoginMessageCodes(_get(auth, 'codes', []))
    }
  } catch (e) {
    setLoginMessages([e.toString()])
    setLoginMessageCodes([-1])
  }
}

function onClickForgotPassword (e, props) {
  const { setOverlayDialogVisible } = props

  e.preventDefault()

  setOverlayDialogVisible(TYPE_FORGOT_PASSWORD)
}

function onLoginSuccess (props, auth) {
  const {
    setOverlayDialogVisible,
    location,
    history,
    resolver,
    user = Map(),
  } = props
  setOverlayDialogVisible(null, false)
  document.activeElement.blur()
  // Remove the language query when we login.
  const query = resolver.get('query', Map())
  const language = query.get('language', List())
  if (language.size > 0) {
    historyRedirect({
      history,
      url: location.pathname,
      auth: fromJS(auth),
      language: user.getIn(['data', 'language']),
      query: _omit(query.toJS(), ['language']),
      historyMethod: HISTORY_METHOD_REPLACE,
    })
  }
}

function onClickRegister (e, props) {
  const {
    onClickClose,
    setEventDefault,
    setOverlayDialogVisible,
  } = props
  e.stopPropagation()

  // GA4 event
  setEventDefault(USER_LOGIN_NEW_TO_GAIA_CLICK_GA4_EVENT)

  setOverlayDialogVisible(null, false)

  if (_isFunction(onClickClose)) {
    onClickClose()
  }
}

function onClickCustomerService (e, props) {
  const {
    onClickClose,
    setOverlayDialogVisible,
  } = props
  e.stopPropagation()
  setOverlayDialogVisible(null, false)

  if (_isFunction(onClickClose)) {
    onClickClose()
  }
}

function isSubmitDisabled (canSubmit, processing) {
  return canSubmit === false || processing === true
}

function setCanSubmit (currentCanSubmit, nextCanSubmit, setLoginCanSubmit) {
  if (currentCanSubmit !== nextCanSubmit) {
    setLoginCanSubmit(nextCanSubmit)
  }
}

function onLoginWithCodeClick (e, props) {
  const {
    setOverlayDialogVisible,
  } = props
  e.stopPropagation()
  setOverlayDialogVisible(TYPE_LOGIN_WITH_CODE)
}

class Login extends Component {
  //
  constructor (props) {
    super(props)
    this.state = {
      termsChecked: false,
    }
    this.loginRef = React.createRef()
  }

  componentDidMount () {
    if (this.loginRef.current && this.props.checkoutEmail) {
      this.loginRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  render () {
    const { props, state } = this
    const {
      loginDialogMessage,
      login,
      auth,
      user,
      loginStaticText,
      setLoginCanSubmit,
      emailText,
      passwordText,
      forgotText,
      helpText,
      location,
      checkoutEmail = null,
    } = props
    const { termsChecked } = state
    const canSubmit = login.get('canSubmit', false)
    const authProcessing = auth.get('processing')
    const messages = login.get('messages')
    const messageCodes = login.get('messageCodes', List())
    const unrecognizedUserNameOrPassword = messageCodes.includes(
      LOGIN_CODE_UNRECOGNIZED_USERNAME_PASSWORD,
    )
    const unrecognizedMessage = `${loginStaticText.getIn(['data', 'unrecognizedUsernameOrPassword'])} www.gaia.com/contact.`

    let supportLink = 'https://gaiasupportcenter.zendesk.com/hc/en-us/requests/new'
    const langIsEs = getPrimary(user.getIn(['data', 'language'], List())) === ES
    const submitDisabled = isSubmitDisabled(canSubmit, authProcessing, termsChecked, location)

    if (langIsEs && _get(features, ['userLanguage', 'esZendeskHelpLink'])) {
      supportLink = URL_HELP_CENTER_ES
    }

    return (
      <div className="login" ref={this.loginRef}>
        <H2 as={HEADING_TYPES.H4} className="login__title">
          {loginStaticText.getIn(['data', 'logIn'])}
        </H2>
        <FormsyForm
          onValidSubmit={_partial(onValidSubmit, _partial.placeholder, props)}
          onValid={_partial(setCanSubmit, canSubmit, true, setLoginCanSubmit)}
          onInvalid={_partial(setCanSubmit, canSubmit, false, setLoginCanSubmit)}
        >
          {messages ? (
            <ErrorMessage
              errorMessages={
                unrecognizedUserNameOrPassword
                  ? [unrecognizedMessage]
                  : messages.toArray()
              }
            />
          ) : (
            <div className="login__error-message-placeholer" />
          )}
          {
            loginDialogMessage ? (
              <p className="login__login-dialog-message">
                { loginDialogMessage }
              </p>
            )
              : null
          }
          <LoginUsername
            name="username"
            validations={{ minLength: 1, maxLength: 60 }}
            required
            customPlaceholder={emailText}
            value={checkoutEmail}
          />
          <LoginPassword
            name="password"
            validations={{ maxLength: 60 }}
            required
            customPlaceholder={passwordText}
            autofocus={!!checkoutEmail}
          />
          <div className="login__submit-wrapper">
            <Button
              type={TYPES.PRIMARY}
              submit
              className="login__login-button"
              disabled={submitDisabled}
              stacked
            >
              {loginStaticText.getIn(['data', 'logIn'])}
            </Button>
            {loginWithCodeEnabled
              ? <>
                <p className="login__text">{loginStaticText.getIn(['data', 'or'])}</p>
                <Button
                  type={TYPES.SECONDARY}
                  className="login__login-code-button"
                  stacked
                  onClick={_partial(onLoginWithCodeClick, _partial.placeholder, props)}
                >
                  {loginStaticText.getIn(['data', 'logInWithCode'])}
                </Button>
              </>
              : null}
          </div>
          <div className="login__forgot-password">
            <Link
              className="login__forgot-password-link"
              to="#forgot-password"
              onClick={_partial(
                onClickForgotPassword,
                _partial.placeholder,
                props,
              )}
            >
              {forgotText || loginStaticText.getIn(['data', 'forgotPassword'])}
            </Link>
          </div>
          <div className="login__register">
            <ButtonSignUp
              className="login__register-link"
              onClick={_partial(onClickRegister, _partial.placeholder, props)}
              type={BUTTON_SIGN_UP_TYPE_LINK}
              text={loginStaticText.getIn(['data', 'newToGaia'])}
              scrollToTop
            />
          </div>
          <div className="login__support">
            <Link
              to={supportLink}
              target={TARGET_BLANK}
              className="login__support-link"
              onClick={_partial(onClickCustomerService, _partial.placeholder, props)}
            >
              {helpText || loginStaticText.getIn(['data', 'contactCustomerService'])}
            </Link>
          </div>
          {authProcessing ? (
            <div className="login__loading">
              <Loading visible icon={LOADING_ICON_BLUE_DARK_BG_WHITE} />
            </div>
          ) : (
            <div className="login__loading-placeholder" />
          )}
        </FormsyForm>
      </div>
    )
  }
}

Login.propTypes = {
  loginDialogMessage: PropTypes.string,
  setLoginCanSubmit: PropTypes.func.isRequired,
  setLoginMessageCodes: PropTypes.func.isRequired,
  setLoginMessages: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  login: ImmutablePropTypes.map.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  app: ImmutablePropTypes.map.isRequired,
  page: ImmutablePropTypes.map.isRequired,
  loginStaticText: ImmutablePropTypes.map.isRequired,
  setAuthData: PropTypes.func.isRequired,
  doAuthLogin: PropTypes.func.isRequired,
  onClickClose: PropTypes.func,
  setOverlayDialogVisible: PropTypes.func.isRequired,
}

export default compose(
  connectRouter(),
  connectRedux(
    (state) => ({
      auth: state.auth,
      user: state.user,
      page: state.page,
      app: state.app,
      resolver: state.resolver,
      login: state.login,
      loginStaticText: state.staticText.getIn(['data', 'login'], Map()),
      cartAccountCreatePageStaticText: state.staticText.getIn(['data', 'cartAccountCreatePage'], Map()),
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setLoginCanSubmit: actions.login.setLoginCanSubmit,
        setLoginMessageCodes: actions.login.setLoginMessageCodes,
        setLoginMessages: actions.login.setLoginMessages,
        setOverlayDialogVisible: actions.dialog.setOverlayDialogVisible,
        setAuthData: actions.auth.setAuthData,
        doAuthLogin: actions.auth.doAuthLogin,
        setEventDefault: actions.eventTracking.setEventDefault,
      }
    },
  ),
)(Login)
